import { StackScreenProps } from "@react-navigation/stack";
import { ProductActionsRootParamList } from "../../../../types";
import {
  useGenerateProductBarcode,
  useResetFormOnRootError,
  useSelectedProductEntityContextProvider,
} from "../../utils";
import { DuplicateProductFormValues } from "../../components/DuplicateProductForm/types";
import { DuplicateProductFormProvider } from "../../components/DuplicateProductForm/DuplicateProductFormProvider";
import { useDuplicateProductFormProvider } from "../../components/DuplicateProductForm/hooks";
import { useFormContext } from "react-hook-form";
import {
  Button,
  Page,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import React from "react";
import { Colors } from "react-native-ui-lib";
import { DuplicateProductForm } from "../../components/DuplicateProductForm";

export type ProductActionsCopyProductScreenProps = StackScreenProps<
  ProductActionsRootParamList,
  "ProductActionsCopyProductScreen"
>;

const ProductActionsCopyProduct =
  ({}: ProductActionsCopyProductScreenProps) => {
    const maybeActionsContext = useDuplicateProductFormProvider();
    const { handleGenerateBarcode } = useGenerateProductBarcode();

    const formContext = useFormContext<DuplicateProductFormValues>();
    const { handleSubmit, formState, setError, reset, watch } = formContext;

    const { isDirty, isLoading, isSubmitting, isValid, isSubmitSuccessful } =
      formState;

    useResetFormOnRootError({
      context: formContext,
    });

    const flashMessagesContext = useFlashMessages();

    const disabled =
      !isDirty ||
      isLoading ||
      isSubmitting ||
      isValid == false ||
      isSubmitSuccessful;

    const handleOnSubmit = React.useCallback(
      async (values: DuplicateProductFormValues) => {
        try {
          await maybeActionsContext?.handleOnSubmit(values);
        } catch (err: any) {
          const msg = err?.message ?? "Something went wrong";
          flashMessagesContext.showMessage({
            message: msg,
            severity: "error",
          });
          setError("root", {
            type: "serverError",
            message: msg,
          });
        }
      },
      []
    );

    const footerMarkup = (
      <Button
        disabled={disabled}
        label="Duplicate Product"
        backgroundColor={Colors.action}
        color="white"
        onPress={handleSubmit(handleOnSubmit)}
      ></Button>
    );

    return (
      <Page title="Duplicate Product" footer={footerMarkup}>
        <DuplicateProductForm onGenerateBarcodePress={handleGenerateBarcode} />
      </Page>
    );
  };

export const ProductActionsCopyProductScreen = (
  props: ProductActionsCopyProductScreenProps
) => {
  const { product } = useSelectedProductEntityContextProvider();

  const defaultValues: Partial<DuplicateProductFormValues> = {
    productId: product.id,
    newTitle: product.name,
    newBarcode: "",
    copyProductTemplate: false,
    copyPricing: true,
    copySupplier: true,
  };

  return (
    <DuplicateProductFormProvider defaultValues={defaultValues}>
      <ProductActionsCopyProduct {...props} />
    </DuplicateProductFormProvider>
  );
};
